/**
 * VariableInspectorControl
 * Read-only debug panel that displays current form values as a key-value table.
 * Value: Record<string, unknown> — the full form values object.
 * Intended for dev/debug use; hidden in production via config.
 */

import React, { useState, memo, useCallback } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import { ChevronDownIcon, ChevronUpIcon } from '@atlas-forms/ui-components-react';

export interface VariableInspectorControlProps {
  control: FormControl;
  value?: Record<string, unknown>;
  mode?: 'edit' | 'view';
}

export const VariableInspectorControl = memo(function VariableInspectorControl({ control, value, mode = 'edit' }: VariableInspectorControlProps) {
  const [expanded, setExpanded] = useState(true);
  const cfg = (control as any).config ?? {};

  // In view mode, hide entirely unless explicitly forced visible
  if (mode === 'view' && !cfg.showInViewMode) return null;

  const entries = Object.entries(value ?? {});

  const wrapStyle: React.CSSProperties = {
    borderRadius: 8,
    border: '1px solid rgba(234,179,8,0.3)',
    background: 'rgba(234,179,8,0.04)',
    fontSize: 12,
    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '7px 12px',
    borderBottom: expanded ? '1px solid rgba(234,179,8,0.2)' : 'none',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const badgeStyle: React.CSSProperties = {
    fontSize: 11,
    padding: '1px 6px',
    borderRadius: 4,
    background: 'rgba(234,179,8,0.15)',
    color: '#eab308',
    fontFamily: 'inherit',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const keyStyle: React.CSSProperties = {
    padding: '5px 12px',
    color: '#94a3b8',
    width: '40%',
    verticalAlign: 'top',
    borderBottom: '1px solid rgba(51,65,85,0.12)',
  };

  const valStyle: React.CSSProperties = {
    padding: '5px 12px',
    color: '#a5f3fc',
    borderBottom: '1px solid rgba(51,65,85,0.12)',
    wordBreak: 'break-all',
  };

  const emptyStyle: React.CSSProperties = {
    padding: '16px 12px',
    color: '#475569',
    textAlign: 'center',
  };

  const renderValue = useCallback((v: unknown): string => {
    if (v === null || v === undefined) return 'null';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }, []);

  return (
    <div data-control-id={control.id} data-control-type="variable-inspector" style={wrapStyle}>
      <div style={headerStyle} onClick={() => setExpanded((e) => !e)}>
        <span style={{ color: '#eab308', fontWeight: 600 }}>
          {control.label ?? 'Variable Inspector'}
          <span style={{ ...badgeStyle, marginLeft: 8 }}>{entries.length} vars</span>
        </span>
        <span style={{ color: '#eab308' }}>
          {expanded ? <ChevronUpIcon width={14} height={14} /> : <ChevronDownIcon width={14} height={14} />}
        </span>
      </div>
      {expanded && (
        entries.length === 0 ? (
          <div style={emptyStyle}>No form values</div>
        ) : (
          <table style={tableStyle}>
            <tbody>
              {entries.map(([k, v]) => (
                <tr key={k}>
                  <td style={keyStyle}>{k}</td>
                  <td style={valStyle}>{renderValue(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
});
